import { Activity, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useMessagesStore } from '@/store/messages.store';
import Button from '@ingka/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import { useAddMessageMutation, useEditMessageMutation } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { MessageTabs } from '@repo/types/messages-tabs';
import type { MessageBannerState } from '@repo/ui';
import { useRegisterCommands } from '@sp-saljapro/command-menu/react';

import { useAssetParams } from '@/hooks/asset.hooks';
import { trackCommand } from '@/utils/command-menu.utils';
import { getAppConfig } from '@/utils/window.utils';

import { AssetMessageModalAdd } from './asset-message-modal-add/asset-message-modal-add.component';
import { AssetMessageModalEdit } from './asset-message-modal-edit/asset-message-modal-edit.component';

interface Props {
    visible: boolean;
    onClose: () => void;
}

export const AssetMessageModal = ({ visible, onClose }: Props) => {
    const { assetId } = useAssetParams();
    const { mutateAsync: editMessage, status: editMessageStatus } = useEditMessageMutation();
    const { mutateAsync: addMessage, status: addMessageStatus } = useAddMessageMutation();

    const [bannerState, setBannerState] = useState<MessageBannerState>('idle');

    const { activeTab, mode, draftMessage, messageId, setMode, setDraftMessage, setMessageId, setActiveTab } =
        useMessagesStore(
            useShallow((state) => ({
                activeTab: state.activeTab,
                mode: state.mode,
                draftMessage: state.draftMessage,
                messageId: state.messageId,
                setMode: state.setMode,
                setDraftMessage: state.setDraftMessage,
                setMessageId: state.setMessageId,
                setActiveTab: state.setActiveTab,
            }))
        );

    const commandMenuEnabled = getAppConfig().feature.commandMenu?.enable;

    useRegisterCommands({
        enabled: commandMenuEnabled,
        commands: [
            {
                id: 'asset:msg-tab-1',
                keys: ['1'],
                hidden: true,
                enabled: () => visible,
                onSelect: (source) => {
                    trackCommand('asset:msg-tab-1', source);
                    setActiveTab(MessageTabs.TSP_CUST);
                },
            },
            {
                id: 'asset:msg-tab-2',
                keys: ['2'],
                hidden: true,
                enabled: () => visible,
                onSelect: (source) => {
                    trackCommand('asset:msg-tab-2', source);
                    setActiveTab(MessageTabs.INTERNAL);
                },
            },
            {
                id: 'asset:msg-tab-3',
                keys: ['3'],
                hidden: true,
                enabled: () => visible,
                onSelect: (source) => {
                    trackCommand('asset:msg-tab-3', source);
                    setActiveTab(MessageTabs.EXTERNAL);
                },
            },
        ],
    });

    const onAdd = async (message: string) => {
        setBannerState('idle');
        try {
            await addMessage({
                message,
                audience: activeTab,
                orderId: assetId,
            });
            setBannerState('add-success');
            return true;
        } catch {
            // todo: log error
            setBannerState('add-error');
            return false;
        }
    };

    const onEdit = async () => {
        setBannerState('idle');
        if (!messageId) {
            return;
        }
        try {
            setMode('add');
            await editMessage({
                audience: activeTab,
                message: draftMessage,
                messageId: messageId,
                orderId: assetId,
            });
            setBannerState('edit-success');
            setDraftMessage('');
            setMessageId(null);
        } catch {
            // todo: log error
            setMode('edit');
            setBannerState('edit-error');
        }
    };

    const onCancel = () => {
        setMode('add');
        setDraftMessage('');
        setMessageId(null);
    };

    return (
        <Modal visible={visible} handleCloseBtn={onClose} data-testid="message-modal">
            <Sheets
                aria-label={translate('messages_modal')}
                header={
                    <ModalHeader
                        title={
                            mode === 'edit'
                                ? translate('messages_modal_edit_message_label')
                                : translate('messages_modal')
                        }
                        {...(mode === 'edit' && {
                            backBtnClick: () => {
                                setMode('add');
                            },
                        })}
                    />
                }
                footer={
                    <Activity mode={mode === 'edit' ? 'visible' : 'hidden'}>
                        <ModalFooter>
                            <Button
                                size="small"
                                type="secondary"
                                text={translate('messages_modal_cancel_button')}
                                onClick={onCancel}
                            />
                            <Button
                                size="small"
                                type="primary"
                                text={translate('messages_modal_save_message_button')}
                                onClick={() => {
                                    void onEdit();
                                }}
                            />
                        </ModalFooter>
                    </Activity>
                }
            >
                <ModalBody>
                    {mode === 'add' && (
                        <AssetMessageModalAdd
                            bannerState={bannerState}
                            setBannerState={setBannerState}
                            addMessageStatus={addMessageStatus}
                            onAdd={onAdd}
                        />
                    )}
                    {mode === 'edit' && <AssetMessageModalEdit bannerState={bannerState} status={editMessageStatus} />}
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
